import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import { Subscription } from 'rxjs';

/**
 * Interactive Popover Component
 *
 * A custom popover that stays open when interacting with its content.
 * Built with Angular CDK Overlay for full control over positioning and behavior.
 *
 * @example
 * ```html
 * <cm-interactive-popover [content]="myTemplate" [placement]="'bottom'">
 *   <button>Click me</button>
 * </cm-interactive-popover>
 *
 * <ng-template #myTemplate>
 *   <div>Interactive content here</div>
 * </ng-template>
 * ```
 */
@Component({
  selector: 'cm-interactive-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './interactive-popover.component.html',
  styleUrl: './interactive-popover.component.less',
})
export class InteractivePopoverComponent implements OnDestroy {
  /**
   * Template content to display in the popover
   */
  readonly content = input.required<TemplateRef<unknown>>();

  /**
   * Placement of the popover relative to trigger
   */
  readonly placement = input<'top' | 'bottom' | 'left' | 'right'>('bottom');

  /**
   * Accessible name for the popover dialog. Announced when focus enters the overlay.
   */
  readonly ariaLabel = input<string>('Popover');

  /**
   * When true, the popover cannot be opened.
   */
  readonly disabled = input<boolean>(false);

  /**
   * Emitted when popover opens
   */
  readonly opened = output<void>();

  /**
   * Emitted when popover closes
   */
  readonly closed = output<void>();

  /**
   * Whether the popover is currently open
   */
  public readonly isOpen = signal<boolean>(false);

  private readonly _overlay = inject(Overlay);
  private readonly _elementRef = inject(ElementRef);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _focusTrapFactory = inject(FocusTrapFactory);

  private _overlayRef: OverlayRef | null = null;
  private _portal: TemplatePortal | null = null;
  private _focusTrap: FocusTrap | null = null;
  private _keydownSub: Subscription | null = null;
  private _triggerEl: HTMLElement | null = null;

  constructor() {
    // Listen for clicks outside to close popover
    effect(() => {
      if (this.isOpen()) {
        setTimeout(() => {
          document.addEventListener('click', this._onDocumentClick);
        }, 0);
      } else {
        document.removeEventListener('click', this._onDocumentClick);
      }
    });

    // Force-close if it becomes disabled while open
    effect(() => {
      if (this.disabled()) {
        this.close();
      }
    });
  }

  /**
   * Get overlay positions based on placement
   */
  private _getPositions(): ConnectedPosition[] {
    const placement = this.placement();

    switch (placement) {
      case 'top':
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
        ];
      case 'bottom':
        return [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
            offsetY: 8,
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
            offsetY: -8,
          },
        ];
      case 'left':
        return [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -8,
          },
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8,
          },
        ];
      case 'right':
        return [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
            offsetX: 8,
          },
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
            offsetX: -8,
          },
        ];
      default:
        return [];
    }
  }

  /**
   * Toggle popover open/close
   */
  toggle(): void {
    if (this.disabled()) return;

    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the popover
   */
  open(): void {
    if (this.isOpen() || this.disabled()) return;

    const positionStrategy = this._overlay
      .position()
      .flexibleConnectedTo(this._elementRef)
      .withPositions(this._getPositions())
      .withPush(true)
      .withViewportMargin(8);

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
    });

    this._portal = new TemplatePortal(this.content(), this._viewContainerRef);
    this._overlayRef.attach(this._portal);

    // The overlay is rendered in the CDK overlay container, outside this component's
    // DOM, so it needs its own dialog semantics, focus trap and Escape handling.
    const overlayEl = this._overlayRef.overlayElement;
    overlayEl.setAttribute('role', 'dialog');
    overlayEl.setAttribute('aria-modal', 'true');
    overlayEl.setAttribute('aria-label', this.ariaLabel());

    this._triggerEl = this._resolveTriggerElement();

    this._focusTrap = this._focusTrapFactory.create(overlayEl);
    this._focusTrap.focusFirstTabbableElementWhenReady();

    // Escape must work while focus is inside the overlay, where trigger keydowns never reach.
    this._keydownSub = this._overlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    });

    this.isOpen.set(true);
    this.opened.emit();
  }

  /**
   * The focusable control inside the projected trigger, used to restore focus on close.
   */
  private _resolveTriggerElement(): HTMLElement | null {
    const host = this._elementRef.nativeElement as HTMLElement;
    const active = document.activeElement as HTMLElement | null;

    if (active && host.contains(active)) return active;

    return host.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
  }

  /**
   * Close the popover
   */
  close(): void {
    if (!this.isOpen()) return;

    this._keydownSub?.unsubscribe();
    this._keydownSub = null;

    this._focusTrap?.destroy();
    this._focusTrap = null;

    if (this._overlayRef) {
      this._overlayRef.detach();
      this._overlayRef.dispose();
      this._overlayRef = null;
    }

    this._portal = null;
    this.isOpen.set(false);

    // Return focus to the trigger so keyboard users keep their place (WCAG 2.4.3).
    this._triggerEl?.focus();
    this._triggerEl = null;

    this.closed.emit();
  }

  /**
   * Handle document clicks to close popover when clicking outside
   */
  private _onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    // Don't close if clicking the trigger
    if (this._elementRef.nativeElement.contains(target)) {
      return;
    }

    // Don't close if clicking inside the popover
    if (this._overlayRef) {
      const overlayElement = this._overlayRef.overlayElement;
      if (overlayElement.contains(target)) {
        return;
      }
    }

    // Close if clicking outside
    this.close();
  };

  /**
   * Handle trigger click
   */
  onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggle();
  }

  /**
   * Handle trigger keydown for accessibility
   */
  onTriggerKeydown(event: KeyboardEvent): void {
    // Enter/Space toggles; Escape closes an open popover and keeps focus on the trigger.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    } else if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._onDocumentClick);
    this.close();
  }
}
